import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { MapPin, Phone, Utensils, Wine } from "lucide-react";
import { useContent, resolveMediaUrl } from "@/context/ContentContext";

export default function LandingPage() {
  const { content } = useContent();
  const img = (slot) => resolveMediaUrl(content?.images?.[slot]);

  return (
    <div className="min-h-screen bg-[#F9F6F0] text-[#1A2B3C]" data-testid="landing-page">
      <SiteHeader />

      {/* HERO */}
      <section className="relative min-h-[100svh] h-screen w-full overflow-hidden" data-testid="hero-section">
        {img("hero") && (
          <img src={img("hero")} alt="" className="absolute inset-0 h-full w-full object-cover" />
        )}
        {/* Very light overlay so the photo (and any text baked into it) stays visible */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/55" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-24">
          <div className="max-w-2xl [text-shadow:_0_2px_16px_rgba(0,0,0,0.65)]">
            <div className="overline text-white/90 mb-6" data-testid="hero-overline">
              {content.hero_overline}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-white">
              {content.hero_title_line1}
              <br />
              {content.hero_title_line2}
            </h1>
            <p className="mt-6 text-white/95 text-base sm:text-lg max-w-xl leading-relaxed">
              {content.hero_subtitle}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/book">
                <Button className="btn-terracotta rounded-full h-12 px-8 text-base" data-testid="hero-book-btn">
                  Reserve a Table
                </Button>
              </Link>
              {content.menu_url && (
                <a
                  href={content.menu_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-white/90 text-sm underline underline-offset-8 hover:text-white"
                  data-testid="hero-menu-link"
                >
                  View the menu →
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section id="story" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10" data-testid="story-section">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5 md:sticky md:top-28 self-start">
            <div className="overline">Our Story</div>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight">
              {content.story_title}
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-6 text-[15px] leading-relaxed text-[#1A2B3C]/90">
            <p>{content.story_paragraph_1}</p>
            <p>{content.story_paragraph_2}</p>
            <div className="grid grid-cols-2 gap-6 pt-6">
              <Feature icon={<Utensils className="h-5 w-5" />} label="Mediterranean cuisine" />
              <Feature icon={<Wine className="h-5 w-5" />} label="Local wine & cocktails" />
              <Feature icon={<MapPin className="h-5 w-5" />} label="Sunset caldera views" />
              <Feature icon={<Phone className="h-5 w-5" />} label="Free parking & bus stop" />
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="pb-24 md:pb-32 max-w-7xl mx-auto px-6 md:px-10" data-testid="gallery-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline">Gallery</div>
            <h2 className="mt-3 font-display text-4xl">A table at golden hour</h2>
          </div>
        </div>

        {/* Mobile: stacked with aspect ratios */}
        <div className="grid grid-cols-2 gap-3 md:hidden">
          {img("gallery_1") && <img src={img("gallery_1")} alt="" className="col-span-2 aspect-[16/10] object-cover w-full rounded-lg" />}
          {img("food") && <img src={img("food")} alt="" className="aspect-square object-cover w-full rounded-lg" />}
          {img("dining") && <img src={img("dining")} alt="" className="aspect-square object-cover w-full rounded-lg" />}
          {img("gallery_2") && <img src={img("gallery_2")} alt="" className="aspect-square object-cover w-full rounded-lg" />}
          {img("gallery_3") && <img src={img("gallery_3")} alt="" className="aspect-square object-cover w-full rounded-lg" />}
        </div>

        {/* Desktop: bento grid */}
        <div className="hidden md:grid grid-cols-6 grid-rows-2 gap-4 h-[520px]">
          {img("gallery_1") && <img src={img("gallery_1")} alt="" className="col-span-3 row-span-2 object-cover w-full h-full rounded-lg" />}
          {img("food") && <img src={img("food")} alt="" className="col-span-2 row-span-1 object-cover w-full h-full rounded-lg" />}
          {img("gallery_2") && <img src={img("gallery_2")} alt="" className="col-span-1 row-span-1 object-cover w-full h-full rounded-lg" />}
          {img("dining") && <img src={img("dining")} alt="" className="col-span-1 row-span-1 object-cover w-full h-full rounded-lg" />}
          {img("gallery_3") && <img src={img("gallery_3")} alt="" className="col-span-2 row-span-1 object-cover w-full h-full rounded-lg" />}
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="bg-white border-t border-[#E5E0D8]" data-testid="visit-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="overline">Visit</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-tight">
              {content.visit_title}
            </h2>
            <p className="mt-5 text-[#1A2B3C]/80 max-w-md leading-relaxed">
              {content.visit_subtitle}
            </p>
            <div className="mt-8">
              <Link to="/book">
                <Button className="btn-terracotta rounded-full h-12 px-8" data-testid="visit-book-btn">
                  Book your sunset table
                </Button>
              </Link>
            </div>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-6">
            <InfoRow label="Address" value={content.address} />
            <InfoRow label="Phone" value={content.phone} />
            <InfoRow label="WhatsApp" value={content.whatsapp} />
            <InfoRow label="Email" value={content.email} />
            <InfoRow label="Hours" value={content.hours} />
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function Feature({ icon, label }) {
  return (
    <div className="flex items-center gap-3 text-sm text-[#1A2B3C]">
      <span className="text-[#D35400]">{icon}</span>
      <span>{label}</span>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-6 items-baseline border-b border-[#E5E0D8] pb-4">
      <div className="overline col-span-1">{label}</div>
      <div className="col-span-2 text-[15px] text-[#1A2B3C] break-words">{value}</div>
    </div>
  );
}
